import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Sun, Moon, Cookie } from 'lucide-react';
import { 
  trackPageView, trackSectionView,
  createSectionObserver, createScrollDepthTracker, createTimeTracker
} from '../utils/analytics';

import { THEMES, DEFAULT_LOGO_URL, ALL_THEMES, effectiveTheme } from '../components/landing/landingConstants';
import { signalPricingCtaArrival } from '../utils/ctaActions';
import { LandingNav } from '../components/landing/LandingNav';
import { BetaBanner } from '../components/landing/BetaBanner';
import { HeroSection } from '../components/landing/HeroSection';
import { Hero2Section } from '../components/landing/Hero2Section';
import { DashboardPreviewSection } from '../components/landing/DashboardPreviewSection';
import { FeaturesSection } from '../components/landing/FeaturesSection';
import { VoiceFeaturesSection } from '../components/landing/VoiceFeaturesSection';
import { CustomizationSection } from '../components/landing/CustomizationSection';
import { VideoDemoSection } from '../components/landing/VideoDemoSection';
import { TestimonialsSection } from '../components/landing/TestimonialsSection';
import { PricingSection } from '../components/landing/PricingSection';
import { PricingSliderSection } from '../components/landing/PricingSliderSection';
import { FAQSection } from '../components/landing/FAQSection';
import { FinalCTASection } from '../components/landing/FinalCTASection';
import { CustomCTASection } from '../components/landing/CustomCTASection';
import { FinalWhistleSection } from '../components/landing/FinalWhistleSection';
import { FoundersCapStickyBand } from '../components/landing/FoundersCapStickyBand';
import { LoyaltyLadderSection } from '../components/landing/LoyaltyLadderSection';
import { PrimaryCTASection } from '../components/landing/PrimaryCTASection';
import { SecondaryCTASection } from '../components/landing/SecondaryCTASection';
import { ContactSection } from '../components/landing/ContactSection';
import { GallerySection } from '../components/landing/GallerySection';
import { SectionChrome } from '../components/landing/sectionChrome';
import { LandingFooter } from '../components/landing/LandingFooter';
import { CookieConsent } from '../components/landing/CookieConsent';
import { MarketingPopup } from '../components/landing/MarketingPopup';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_ORDER = [
  { id: 'dashboard_preview', visible: true },
  { id: 'voice_features', visible: true },
  { id: 'hero2', visible: false },
  { id: 'features', visible: true },
  { id: 'customization', visible: true },
  { id: 'video', visible: true },
  { id: 'gallery', visible: true },
  { id: 'testimonials', visible: true },
  { id: 'pricing', visible: true },
  { id: 'faq', visible: true },
  { id: 'primary_cta', visible: true },
  { id: 'secondary_cta', visible: false },
  { id: 'contact', visible: true },
  { id: 'final_cta', visible: true },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [pricingCurrency, setPricingCurrency] = useState(() => {
    try {
      const saved = localStorage.getItem('pricing_currency');
      if (saved && ['eur', 'gbp', 'usd'].includes(saved)) return saved;
      // Detect visitor's country from browser locale (e.g. 'en-GB' → 'GB')
      const locale = navigator.language || 'en-GB';
      const region = (new Intl.Locale(locale).region || locale.split('-')[1] || '').toUpperCase();
      // Map country code → currency
      const GBP = ['GB', 'UK', 'IM', 'GG', 'JE'];
      const USD = ['US', 'CA', 'MX', 'AU', 'NZ', 'SG', 'HK', 'JP', 'CN', 'IN', 'BR', 'AR', 'ZA'];
      if (GBP.includes(region)) return 'gbp';
      if (USD.includes(region)) return 'usd';
      return 'eur'; // EU + everything else
    } catch {
      return 'eur';
    }
  });
  // Persist user's explicit choice so we don't re-detect on next visit
  const setPricingCurrencyPersist = (c) => {
    try { localStorage.setItem('pricing_currency', c); } catch { /* ignore */ }
    setPricingCurrency(c);
  };
  const [cookieSettingsOpen, setCookieSettingsOpen] = useState(false);
  const [hasConsent, setHasConsent] = useState(() => !!localStorage.getItem('cookie_consent_time'));
  // Live discount % from the Pricing Slider — mirrored onto the Loyalty
  // Ladder's "Today" dot so visitors see their day-1 savings in the same
  // visual language as the multi-year loyalty progression.
  const [pricingSavingsPct, setPricingSavingsPct] = useState(0);
  const [theme, setTheme] = useState(() => localStorage.getItem('landingTheme') || 'dark');
  // First-time visitors: apply the admin's default theme once content has
  // loaded (unless localStorage already holds a returning user's choice).
  // ALL_THEMES guard keeps a typo'd CMS value from leaving the page broken.
  useEffect(() => {
    if (!content?.branding) return;
    if (localStorage.getItem('landingTheme')) return; // returning visitor — respect their choice
    const defaultTheme = content.branding.defaultTheme;
    if (defaultTheme && ALL_THEMES.includes(defaultTheme) && defaultTheme !== theme) {
      setTheme(defaultTheme);
    }
    // intentionally omit `theme` from deps — only run when content arrives
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content?.branding?.defaultTheme]);
  const [videoOpacity] = useState(1);
  const [heroVideoPlaying, setHeroVideoPlaying] = useState(false);
  const [heroPosterLoaded, setHeroPosterLoaded] = useState(false);
  // Soft-launch banner state — single fetch shared by BetaBanner + PricingSection
  // so we don't duplicate the public read across components.
  const [betaMode, setBetaMode] = useState({ enabled: false, message: '' });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/landing/beta-mode`);
        if (!cancelled) setBetaMode({ enabled: !!data.enabled, message: data.message || '' });
      } catch (_) { /* public read — silent failures */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const mobileMenuRef = useRef(null);
  const sectionObserverRef = useRef(null);
  const scrollTrackerRef = useRef(null);
  const timeTrackerRef = useRef(null);

  // Referral capture — when a visitor lands here via a public coach's
  // share link (`/?ref=<slug>`), stash the slug in localStorage so the
  // eventual signup POST can credit that coach. Cleared automatically
  // after a successful register (`AuthContext.register`).
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const ref = params.get('ref');
      if (ref && /^[a-z0-9\-]{3,64}$/i.test(ref)) {
        localStorage.setItem('gc-referral-slug', ref.toLowerCase());
      }
    } catch { /* private mode — silently skip */ }
  }, []);

  const t = THEMES[theme] || THEMES.dark;
  // Effective theme groups `black` with `dark` so the 60+ binary checks in
  // child components (`theme === 'dark'`) Just Work without refactoring.
  const eTheme = effectiveTheme(theme);
  const goToSignup = () => navigate('/onboarding');

  const logoUrl = content?.branding?.logoUrl || DEFAULT_LOGO_URL;
  const appName = content?.branding?.appName || 'Grassroots Coaching';
  const backgroundVideoUrlDesktop = content?.branding?.backgroundVideoUrl || null;
  const backgroundVideoUrlMobileSetting = content?.branding?.backgroundVideoUrlMobile || null;
  const [isMobileViewport, setIsMobileViewport] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 767px)');
    const handler = (e) => setIsMobileViewport(e.matches);
    mq.addEventListener?.('change', handler);
    return () => mq.removeEventListener?.('change', handler);
  }, []);
  const backgroundVideoUrl = (isMobileViewport && backgroundVideoUrlMobileSetting) || backgroundVideoUrlDesktop;
  const videoOpacityDark = (content?.branding?.videoOpacityDark ?? 30) / 100;
  const videoOpacityLight = (content?.branding?.videoOpacityLight ?? 20) / 100;
  const baseVideoOpacity = eTheme === 'dark' ? videoOpacityDark : videoOpacityLight;
  // Resolve the poster image for the active video so the hero renders an
  // instant first-frame image while the video file buffers. Falls back to
  // `/poster` sub-route for raw /api/videos/{id} URLs if no explicit entry.
  const backgroundVideoPoster = (() => {
    if (!backgroundVideoUrl) return null;
    const list = content?.branding?.backgroundVideos || [];
    const match = list.find(v => v.url === backgroundVideoUrl);
    if (match?.posterUrl) {
      return match.posterUrl.startsWith('/api')
        ? `${process.env.REACT_APP_BACKEND_URL}${match.posterUrl}`
        : match.posterUrl;
    }
    const idMatch = backgroundVideoUrl.match(/\/api\/videos\/([^/?#]+)/);
    if (idMatch) return `${process.env.REACT_APP_BACKEND_URL}/api/videos/${idMatch[1]}/poster`;
    return null;
  })();

  // Smooth scroll to section — collapses drawer instantly and scrolls after reflow
  const scrollToSection = (e, sectionId) => {
    e.preventDefault();
    // Snap drawer closed instantly (no animation) so the page layout is settled
    // before we measure scroll target. Necessary because the nav is sticky and
    // drawer expansion pushes content down; otherwise the first scroll lands
    // short by the drawer height.
    if (mobileMenuRef.current) {
      mobileMenuRef.current.style.transition = 'none';
      mobileMenuRef.current.style.maxHeight = '0px';
      mobileMenuRef.current.style.opacity = '0';
      void mobileMenuRef.current.offsetHeight; // force reflow
    }
    setMobileMenuOpen(false);

    // Defer the scroll to the next frame so the forced collapse has painted
    // and getBoundingClientRect reflects the closed-drawer layout.
    requestAnimationFrame(() => {
      const el = document.getElementById(sectionId);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
      // Signal pricing arrival so the slider can pulse (no prefill — nav
      // clicks always show the visitor the default 4-week slider).
      signalPricingCtaArrival(sectionId);
      requestAnimationFrame(() => {
        if (mobileMenuRef.current) {
          mobileMenuRef.current.style.transition = '';
          mobileMenuRef.current.style.maxHeight = '';
          mobileMenuRef.current.style.opacity = '';
        }
      });
    });
  };

  // The admin chooses which themes appear in the public switcher via the
  // Branding CMS tab (`enabledThemes`). Default to dark + light if unset,
  // and always ensure the saved theme stays available even if disabled.
  const enabledThemes = (() => {
    const raw = content?.branding?.enabledThemes;
    const list = Array.isArray(raw) && raw.length >= 1 ? raw.filter(x => ALL_THEMES.includes(x)) : ['dark', 'light'];
    // Always include the currently-saved theme so a freshly-toggled-off
    // theme doesn't leave a user stranded with no switch.
    if (!list.includes(theme) && ALL_THEMES.includes(theme)) list.unshift(theme);
    return list.length > 0 ? list : ['dark', 'light'];
  })();

  const toggleTheme = () => {
    const idx = enabledThemes.indexOf(theme);
    const nextTheme = enabledThemes[(idx + 1) % enabledThemes.length];
    setTheme(nextTheme);
    localStorage.setItem('landingTheme', nextTheme);
  };

  // Reset hero load flags whenever the active video URL changes so the
  // poster fades back in for the new video.
  useEffect(() => {
    setHeroPosterLoaded(false);
    setHeroVideoPlaying(false);
  }, [backgroundVideoPoster, backgroundVideoUrl]);

  // Analytics trackers
  useEffect(() => {
    trackPageView('landing');
    scrollTrackerRef.current = createScrollDepthTracker();
    scrollTrackerRef.current.start();
    timeTrackerRef.current = createTimeTracker();
    timeTrackerRef.current.start();
    return () => {
      scrollTrackerRef.current?.stop();
      timeTrackerRef.current?.stop();
    };
  }, []);

  const handleSectionVisible = useCallback((sectionId) => { trackSectionView(sectionId); }, []);

  // Preload only the first gallery image so it's ready when the user reaches
  // the gallery section. Avoid batch-preloading all images / the background
  // video — it steals bandwidth from the hero video and actually slows the
  // page's first impression.
  useEffect(() => {
    const images = (content?.gallery?.images || []).filter((i) => i && i.url);
    if (images.length === 0) return;
    const backend = process.env.REACT_APP_BACKEND_URL;
    const first = images[0];
    const href = first.url.startsWith('/api') ? `${backend}${first.url}` : first.url;
    // Warm the HTTP cache with a low-priority probe — no <link rel=preload>
    // so the browser doesn't promote it over the video.
    const probe = new window.Image();
    probe.loading = 'lazy';
    probe.src = href;
  }, [content]);

  useEffect(() => {
    sectionObserverRef.current = createSectionObserver(handleSectionVisible);
    document.querySelectorAll('[data-section]').forEach(s => sectionObserverRef.current.observe(s));
    return () => sectionObserverRef.current?.disconnect();
  }, [content, handleSectionVisible]);

  // Load content
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/landing/content`);
        setContent(res.data);
      } catch (err) {
        console.error('Failed to load landing content:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // SEO meta tags
  useEffect(() => {
    if (!content?.seo) return;
    document.title = content.seo.title || 'Grassroots Coaching - Team Management Made Simple';
    const setMeta = (name, val, property = false) => {
      const attr = property ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attr}="${name}"]`);
      if (!meta) { meta = document.createElement('meta'); meta.setAttribute(attr, name); document.head.appendChild(meta); }
      meta.setAttribute('content', val);
    };
    setMeta('description', content.seo.description || 'The simple, customizable team management app built specifically for grassroots coaches.');
    setMeta('keywords', content.seo.keywords || 'grassroots football, team management, coaching app');
    setMeta('og:type', 'website', true);
    setMeta('og:title', content.seo.ogTitle || content.seo.title || 'Grassroots Coaching', true);
    setMeta('og:description', content.seo.ogDescription || content.seo.description || '', true);
    if (content.seo.ogImage) setMeta('og:image', content.seo.ogImage, true);
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', content.seo.twitterTitle || content.seo.title || 'Grassroots Coaching');
    setMeta('twitter:description', content.seo.twitterDescription || content.seo.description || '');
    setMeta('robots', 'index, follow');
    setMeta('author', 'Grassroots Coaching');
  }, [content]);

  if (loading) {
    return (
      <div className={`min-h-screen ${t.bg} flex items-center justify-center`}>
        <div className="w-16 h-16 border-4 border-green-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!content) {
    return (
      <div className={`min-h-screen ${t.bg} flex items-center justify-center ${t.text}`}>
        <p>Failed to load content</p>
      </div>
    );
  }

  const sectionMap = {
    dashboard_preview: <DashboardPreviewSection key="dashboard_preview" theme={eTheme} t={t} logoUrl={logoUrl} appName={appName} />,
    features: <FeaturesSection key="features" content={content} theme={eTheme} t={t} />,
    voice_features: <VoiceFeaturesSection key="voice_features" content={content} theme={eTheme} t={t} />,
    hero2: <Hero2Section key="hero2" content={content} theme={eTheme} t={t} />,
    customization: <CustomizationSection key="customization" content={content} theme={eTheme} t={t} />,
    video: <VideoDemoSection key="video" content={content} theme={eTheme} t={t} />,
    gallery: <GallerySection key="gallery" content={content} theme={eTheme} t={t} />,
    testimonials: <TestimonialsSection key="testimonials" content={content} theme={eTheme} t={t} />,
    pricing: (content.pricing?.mode === 'slider'
      ? (
        <>
          <PricingSliderSection key="pricing" content={content} theme={eTheme} t={t} pricingCurrency={pricingCurrency} setPricingCurrency={setPricingCurrencyPersist} goToSignup={goToSignup} betaMode={betaMode} onSavingsPctChange={setPricingSavingsPct} />
          <LoyaltyLadderSection key="loyalty_ladder" content={content} theme={eTheme} todayDiscountPct={pricingSavingsPct} />
        </>
      )
      : (
        <>
          <PricingSection key="pricing" content={content} theme={eTheme} t={t} pricingCurrency={pricingCurrency} setPricingCurrency={setPricingCurrencyPersist} goToSignup={goToSignup} betaMode={betaMode} />
          <LoyaltyLadderSection key="loyalty_ladder" content={content} theme={eTheme} todayDiscountPct={pricingSavingsPct} />
        </>
      )),
    faq: <FAQSection key="faq" content={content} theme={eTheme} t={t} />,
    primary_cta: <PrimaryCTASection key="primary_cta" content={content} goToSignup={goToSignup} />,
    secondary_cta: <SecondaryCTASection key="secondary_cta" content={content} goToSignup={goToSignup} />,
    contact: <ContactSection key="contact" content={content} theme={eTheme} t={t} />,
    final_cta: <FinalCTASection key="final_cta" content={content} goToSignup={goToSignup} />,
  };

  // Register each admin-created custom CTA as its own entry in sectionMap
  // keyed by its dynamic id (e.g. `custom_cta_a1b2c3d4`). The Layout tab
  // adds these ids to section_order so they render in the chosen position.
  (content.custom_ctas || []).forEach((cta) => {
    if (cta?.id) {
      sectionMap[cta.id] = (
        <CustomCTASection key={cta.id} cta={cta} goToSignup={goToSignup} />
      );
    }
  });

  const saved = content.section_order || [];
  const savedIds = new Set(saved.map(s => s.id));
  // Merge saved order with defaults. New default sections (not present in saved) are spliced in
  // at their DEFAULT_ORDER index relative to the previous saved neighbour, instead of appended
  // at the tail. This preserves positional intent for newly-added sections.
  let order;
  if (saved.length === 0) {
    order = DEFAULT_ORDER;
  } else {
    const merged = [...saved];
    DEFAULT_ORDER.forEach((def, defIdx) => {
      if (savedIds.has(def.id)) return;
      // Find the nearest previous default section that IS in saved — insert after it.
      let insertAfterId = null;
      for (let k = defIdx - 1; k >= 0; k -= 1) {
        if (savedIds.has(DEFAULT_ORDER[k].id)) {
          insertAfterId = DEFAULT_ORDER[k].id;
          break;
        }
      }
      const targetIdx = insertAfterId
        ? merged.findIndex(s => s.id === insertAfterId) + 1
        : 0;
      merged.splice(targetIdx, 0, def);
      savedIds.add(def.id);
    });
    order = merged;
  }
  const visibleOrder = order.filter(s => s.visible);
  const firstVisibleId = visibleOrder[0]?.id;

  return (
    <div className={`min-h-screen ${t.bg} ${t.text} relative`} style={{ overflowX: 'clip' }}>
      <style>{`html { scroll-behavior: smooth; } section[id] { scroll-margin-top: ${content?.branding?.nav_auto_hide ? '0.5rem' : '4.5rem'}; }`}</style>

      {/* Video Background */}
      {backgroundVideoUrl && (() => {
        // CMS-controllable blur. `videoBlur` toggles the effect; `videoBlurAmount`
        // is the pixel radius (1-30). Blur is applied to both the poster
        // image and the live video so they stay visually identical during
        // the poster→video handoff. The wrapper's `overflow-hidden` clips
        // soft edges that would otherwise leak from the blur convolution.
        const blurOn = content?.branding?.videoBlur === true;
        const blurPx = Math.max(0, Math.min(40, Number(content?.branding?.videoBlurAmount ?? 8)));
        const blurFilter = blurOn ? `blur(${blurPx}px)` : undefined;
        return (
        <div className="fixed top-0 left-0 w-screen z-0 overflow-hidden pointer-events-none transition-opacity duration-300 bg-black" style={{ opacity: videoOpacity, height: '100lvh' }}>
          {backgroundVideoPoster && (
            <img
              key={`poster-${backgroundVideoPoster}`}
              src={backgroundVideoPoster}
              alt=""
              aria-hidden="true"
              className="absolute min-w-full min-h-full object-cover transition-opacity duration-700 ease-out"
              style={{
                top: '50%', left: '50%',
                // Scale slightly when blurring so the soft edge fade falls
                // outside the wrapper's clip box (no halo around viewport).
                transform: `translate(-50%, -50%)${blurOn ? ` scale(${1 + blurPx / 200})` : ''}`,
                opacity: heroPosterLoaded && !heroVideoPlaying ? baseVideoOpacity : 0,
                filter: blurFilter,
                willChange: blurOn ? 'filter' : undefined,
              }}
              onLoad={() => setHeroPosterLoaded(true)}
              onError={() => setHeroPosterLoaded(false)}
              data-testid="hero-background-poster"
            />
          )}
          <video
            key={`video-${backgroundVideoUrl}`}
            autoPlay loop muted playsInline preload="metadata"
            poster={backgroundVideoPoster || undefined}
            className="absolute min-w-full min-h-full object-cover transition-opacity duration-700 ease-out"
            style={{
              top: '50%', left: '50%',
              transform: `translate(-50%, -50%)${blurOn ? ` scale(${1 + blurPx / 200})` : ''}`,
              opacity: heroVideoPlaying ? baseVideoOpacity : 0,
              filter: blurFilter,
              willChange: blurOn ? 'filter' : undefined,
            }}
            onPlaying={() => setHeroVideoPlaying(true)}
            onLoadedMetadata={(e) => { e.target.playbackRate = content?.branding?.videoPlaybackSpeed ?? 0.5; }}
            data-testid="hero-background-video"
          >
            <source src={backgroundVideoUrl} type="video/mp4" />
          </video>
          <div className={`absolute inset-0 ${eTheme === 'dark' ? (theme === 'black' ? 'bg-[#111111]/30' : 'bg-gray-900/30') : 'bg-[#111111]/30'}`} />
        </div>
        );
      })()}

      {/* Theme Toggle & Cookie Settings */}
      <div className="fixed bottom-6 left-6 z-50 flex gap-2">
        {content?.branding?.showThemeSwitcher !== false && enabledThemes.length > 1 && (() => {
          const idx = enabledThemes.indexOf(theme);
          const nextTheme = enabledThemes[(idx + 1) % enabledThemes.length];
          const nextLabel = THEMES[nextTheme]?.name || nextTheme;
          // Button styling per-current-theme so it always looks deliberate.
          const btnCls = theme === 'light'
            ? 'bg-[#007a33] text-white hover:bg-[#005c26]'
            : theme === 'black'
              ? 'bg-[#1a1a1a] text-[#00994d] hover:bg-[#262626] border border-[#262626]'
              : 'bg-gray-800 text-yellow-400 hover:bg-gray-700';
          // Icon picks: black → palette swatch icon, light → Sun, dark → Moon
          const NextIcon = theme === 'light' ? Moon : Sun;
          return (
            <button
              onClick={toggleTheme}
              className={`p-3 rounded-full shadow-lg transition-all ${btnCls}`}
              title={`Switch to ${nextLabel} theme`}
              data-testid="theme-toggle"
            >
              <NextIcon className="w-5 h-5" />
            </button>
          );
        })()}
        {hasConsent && (
          <button
            onClick={() => setCookieSettingsOpen(true)}
            className={`p-3 rounded-full shadow-lg transition-all ${
              eTheme === 'dark' ? 'bg-gray-800 text-[#00994d] hover:bg-gray-700' : 'bg-[#007a33] text-white hover:bg-[#005c26]'
            }`}
            title="Cookie settings"
            data-testid="cookie-settings-btn"
          >
            <Cookie className="w-5 h-5" />
          </button>
        )}
      </div>

      <BetaBanner betaMode={betaMode} />

      {/* When the saved theme is `light`, the user wants the HERO ONLY to
          look identical to the black theme (dark bg/overlay/text on the
          video). Rest of the page stays in light. We achieve this by
          passing the black palette + dark binary flag down to HeroSection
          only — every other section (including the nav) still receives
          the real theme so the page below the hero feels light. */}
      <LandingNav
        content={content} theme={eTheme} t={t} user={user}
        mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen}
        mobileMenuRef={mobileMenuRef} scrollToSection={scrollToSection}
        navigate={navigate} goToSignup={goToSignup} logoUrl={logoUrl} appName={appName}
      />

      <HeroSection
        content={content}
        theme={theme === 'light' ? 'dark' : eTheme}
        t={theme === 'light' ? THEMES.black : t}
        goToSignup={goToSignup}
      />

      {/* Dynamic Sections */}
      {visibleOrder.map(s => {
        const node = sectionMap[s.id];
        if (!node) return null;
        return (
          <SectionChrome
            key={s.id}
            id={s.id}
            isFirst={s.id === firstVisibleId}
            theme={theme}
            content={content}
          >
            {node}
          </SectionChrome>
        );
      })}

      {/* Final Whistle — supplemental warm sign-off below Final CTA, before footer.
          Off by default; CMS toggle in CtaFooterTab. */}
      <FinalWhistleSection content={content} theme={theme} goToSignup={goToSignup} />
      <LandingFooter content={content} logoUrl={logoUrl} appName={appName} />
      {/* Sticky Founders' Cap scarcity band — surfaces once visitor has scrolled
          past the Pricing slider. Off by default; CMS toggle in PricingSliderSettings. */}
      <FoundersCapStickyBand content={content} />
      <CookieConsent content={content} theme={theme} forceOpen={cookieSettingsOpen} onClose={() => { setCookieSettingsOpen(false); setHasConsent(true); }} />
      <MarketingPopup content={content} theme={theme} goToSignup={goToSignup} />
    </div>
  );
}
